import React, { useState, useEffect } from 'react';
import { X, Settings, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { BACKEND_CONFIG } from '../utils/productionConfig';

interface EnvConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EnvConfigModal: React.FC<EnvConfigModalProps> = ({ isOpen, onClose }) => {
  const [backendUrl, setBackendUrl] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Load saved backend URL from localStorage
    const savedUrl = localStorage.getItem('BACKEND_URL') || '';
    setBackendUrl(savedUrl);
  }, [isOpen]);

  const handleSave = () => {
    // Remove trailing slashes
    const cleanUrl = backendUrl.trim().replace(/\/+$/, '');
    
    if (cleanUrl) {
      localStorage.setItem('BACKEND_URL', cleanUrl);
      console.log('✅ Backend URL saved:', cleanUrl);
      alert('Settings saved! Please refresh the page for changes to take effect.');
      setHasChanges(false);
    } else {
      localStorage.removeItem('BACKEND_URL');
      console.log('✅ Backend URL cleared');
      alert('Settings cleared! The app will use default localhost URL. Please refresh the page.');
      setHasChanges(false);
    }
  };

  const handleTest = async () => {
    if (!backendUrl.trim()) {
      setTestStatus('error');
      setTestMessage('Please enter a backend URL first');
      return;
    }

    setTestStatus('testing');
    setTestMessage('Testing connection...');

    try {
      const cleanUrl = backendUrl.trim().replace(/\/+$/, '');
      const testUrl = cleanUrl.endsWith('/api') ? cleanUrl : `${cleanUrl}/api`;
      
      const response = await fetch(`${testUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setTestStatus('success');
        setTestMessage('Connection successful! Backend is reachable.');
      } else {
        setTestStatus('error');
        setTestMessage(`Connection failed: ${response.status} ${response.statusText}`);
      }
    } catch (error: any) {
      setTestStatus('error');
      setTestMessage(`Connection failed: ${error.message || 'Network error'}. Make sure the backend is running and CORS is enabled.`);
    }
  };

  const handleReset = () => {
    localStorage.removeItem('BACKEND_URL');
    setBackendUrl('');
    setTestStatus('idle');
    setTestMessage('');
    setHasChanges(false);
    alert('Settings reset! Please refresh the page.');
  };

  const handleUrlChange = (value: string) => {
    setBackendUrl(value);
    setHasChanges(true);
    setTestStatus('idle');
    setTestMessage('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl">Environment Configuration</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="flex items-center gap-2 text-blue-900 mb-2">
              <AlertCircle className="w-5 h-5" />
              About This Configuration
            </h3>
            <p className="text-blue-800 text-sm">
              Configure your backend URL to test with your real API server. This setting is stored in your browser's localStorage and will persist across sessions.
            </p>
          </div>

          {/* Backend URL Input */}
          <div>
            <label className="block text-sm mb-2">
              Backend URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={backendUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder={`e.g., ${BACKEND_CONFIG.url} or https://your-backend.com`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the base URL without /api (it will be added automatically)
            </p>
          </div>

          {/* Current Configuration */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm">Current Configuration:</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Saved URL:</span>
                <span className="font-mono text-xs">
                  {localStorage.getItem('BACKEND_URL') || 'Not set (using default)'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active API URL:</span>
                <span className="font-mono text-xs">
                  {backendUrl ? (backendUrl.endsWith('/api') ? backendUrl : `${backendUrl}/api`) : BACKEND_CONFIG.apiUrl}
                </span>
              </div>
            </div>
          </div>

          {/* Test Connection */}
          {testStatus !== 'idle' && (
            <div
              className={`rounded-lg p-4 flex items-start gap-3 ${
                testStatus === 'testing'
                  ? 'bg-blue-50 border border-blue-200'
                  : testStatus === 'success'
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              {testStatus === 'testing' && (
                <RefreshCw className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
              )}
              {testStatus === 'success' && (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              )}
              {testStatus === 'error' && (
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p
                  className={`text-sm ${
                    testStatus === 'testing'
                      ? 'text-blue-800'
                      : testStatus === 'success'
                      ? 'text-green-800'
                      : 'text-red-800'
                  }`}
                >
                  {testMessage}
                </p>
              </div>
            </div>
          )}

          {/* Examples */}
          <div className="border-t pt-4">
            <h3 className="text-sm mb-2">Examples:</h3>
            <div className="space-y-2">
              <button
                onClick={() => handleUrlChange(BACKEND_CONFIG.url)}
                className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded text-sm font-mono transition-colors"
              >
                {BACKEND_CONFIG.url}
              </button>
              <button
                onClick={() => handleUrlChange('https://api.yourapp.com')}
                className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded text-sm font-mono transition-colors"
              >
                https://api.yourapp.com
              </button>
              <button
                onClick={() => handleUrlChange('http://192.168.1.100:5001')}
                className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded text-sm font-mono transition-colors"
              >
                http://192.168.1.100:5001 (Local network)
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Reset to Default
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleTest}
              disabled={!backendUrl.trim() || testStatus === 'testing'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {testStatus === 'testing' ? 'Testing...' : 'Test Connection'}
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Save & Reload Required
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
